import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type EdgeChange,
  type NodeChange,
  type Viewport,
  type XYPosition,
} from "@xyflow/react";
import { create } from "zustand";
import {
  applyBoardChanges,
  createBoardRecord,
  createDefaultProject,
  deleteBoardRecord,
  duplicateBoardTree,
  getBoardSnapshot,
  initializeDefaultProject,
  kimnoteDb,
  putAssetBlob,
  setBoardParent,
  type BoardChanges,
} from "../db/database";
import {
  canvasEdgeToStoredEdge,
  canvasNodeToStoredNode,
  nowIso,
  storedEdgeToCanvasEdge,
  storedNodeToCanvasNode,
  type BoardRecord,
  type ProjectRecord,
} from "../core/model";
import { componentRegistry } from "../core/component-registry";
import {
  createEditorNode,
  type ClipboardGraph,
  type EditorEdge,
  type EditorNode,
  type EditorNodeData,
} from "../core/editor-types";
import {
  NodeSnapIndex,
  snapNodePosition,
  snapNodeResize,
  type ResizeEdges,
  type SnapGuide,
  type SnapRect,
} from "../core/node-snapping";
import {
  bestConnectionHandles,
  reassignConnectionHandles,
} from "../core/connection-geometry";

type EditorStatus = "idle" | "loading" | "ready" | "error";

interface HistoryFrame {
  label: string;
  nodes: EditorNode[];
  edges: EditorEdge[];
}

interface EditorState {
  status: EditorStatus;
  error: string | null;
  projects: ProjectRecord[];
  boards: BoardRecord[];
  project: ProjectRecord | null;
  board: BoardRecord | null;
  nodes: EditorNode[];
  edges: EditorEdge[];
  saving: boolean;
  past: HistoryFrame[];
  future: HistoryFrame[];
  clipboard: ClipboardGraph | null;
  alignmentGuides: SnapGuide[];
  boot: () => Promise<void>;
  createProject: (title?: string) => Promise<void>;
  selectProject: (projectId: string) => Promise<void>;
  renameProject: (title: string) => void;
  createBoard: (title?: string) => Promise<void>;
  selectBoard: (boardId: string) => Promise<void>;
  renameBoard: (title: string) => void;
  moveBoard: (boardId: string, parentId: string | null) => Promise<void>;
  duplicateBoard: (boardId: string) => Promise<BoardRecord | null>;
  deleteBoard: (boardId: string) => Promise<void>;
  refreshLibrary: () => Promise<void>;
  addNode: (kind: string, position?: XYPosition, overrides?: Partial<EditorNodeData>) => EditorNode | null;
  addFiles: (files: readonly File[], position: XYPosition) => Promise<void>;
  updateNodeData: (id: string, patch: Partial<EditorNodeData>, historyLabel?: string) => void;
  updateEdge: (id: string, patch: { label?: string; color?: string; animated?: boolean }) => void;
  onNodesChange: (changes: NodeChange<EditorNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<EditorEdge>[]) => void;
  onConnect: (connection: Connection) => void;
  beginGesture: (label: string) => void;
  commitGesture: () => void;
  beginNodeDrag: (nodeId: string, snapThreshold: number, snapGap: number) => void;
  endNodeDrag: () => void;
  beginNodeResize: (nodeId: string, snapThreshold: number, snapGap: number, keepAspectRatio?: boolean) => void;
  endNodeResize: () => void;
  saveViewport: (viewport: Viewport) => void;
  deleteSelected: () => void;
  duplicateSelected: () => Promise<void>;
  copySelected: () => void;
  pasteClipboard: (position?: XYPosition) => void;
  selectAll: () => void;
  clearSelection: () => void;
  undo: () => void;
  redo: () => void;
  flush: () => Promise<void>;
}

const HISTORY_LIMIT = 48;
const SAVE_DELAY_MS = 320;
const TEXT_HISTORY_WINDOW_MS = 900;

interface PendingBoardChanges {
  nodes: Map<string, ReturnType<typeof canvasNodeToStoredNode>>;
  edges: Map<string, ReturnType<typeof canvasEdgeToStoredEdge>>;
  deleteNodeIds: Set<string>;
  deleteEdgeIds: Set<string>;
  viewport?: Viewport;
  timer?: ReturnType<typeof setTimeout>;
}

const pendingByBoard = new Map<string, PendingBoardChanges>();
let activeGesture: HistoryFrame | null = null;
interface ActiveSnapSession {
  anchorId: string;
  movingIds: Set<string>;
  index: NodeSnapIndex;
  threshold: number;
  gap: number;
  mode: "drag" | "resize";
  resizeEdges: ResizeEdges;
  keepAspectRatio: boolean;
}
let activeSnapSession: ActiveSnapSession | null = null;
let lastHistoryKey = "";
let lastHistoryAt = 0;

function pendingFor(boardId: string): PendingBoardChanges {
  let pending = pendingByBoard.get(boardId);
  if (!pending) {
    pending = {
      nodes: new Map(),
      edges: new Map(),
      deleteNodeIds: new Set(),
      deleteEdgeIds: new Set(),
    };
    pendingByBoard.set(boardId, pending);
  }
  return pending;
}

function enqueueChanges(boardId: string, changes: BoardChanges): void {
  const pending = pendingFor(boardId);
  for (const node of changes.upsertNodes ?? []) {
    pending.deleteNodeIds.delete(node.id);
    pending.nodes.set(node.id, node);
  }
  for (const edge of changes.upsertEdges ?? []) {
    pending.deleteEdgeIds.delete(edge.id);
    pending.edges.set(edge.id, edge);
  }
  for (const id of changes.deleteNodeIds ?? []) {
    pending.nodes.delete(id);
    pending.deleteNodeIds.add(id);
  }
  for (const id of changes.deleteEdgeIds ?? []) {
    pending.edges.delete(id);
    pending.deleteEdgeIds.add(id);
  }
  if (changes.viewport) pending.viewport = changes.viewport;
  if (pending.timer) clearTimeout(pending.timer);
  pending.timer = setTimeout(() => void flushBoard(boardId), SAVE_DELAY_MS);
  useEditorStore.setState({ saving: true });
}

async function flushBoard(boardId: string): Promise<void> {
  const pending = pendingByBoard.get(boardId);
  if (!pending) return;
  if (pending.timer) clearTimeout(pending.timer);
  pendingByBoard.delete(boardId);
  try {
    await applyBoardChanges(boardId, {
      upsertNodes: [...pending.nodes.values()],
      upsertEdges: [...pending.edges.values()],
      deleteNodeIds: [...pending.deleteNodeIds],
      deleteEdgeIds: [...pending.deleteEdgeIds],
      viewport: pending.viewport,
    });
    useEditorStore.setState({ saving: pendingByBoard.size > 0 });
  } catch (error) {
    useEditorStore.setState({
      saving: false,
      error: error instanceof Error ? error.message : "保存失败",
    });
  }
}

async function flushAll(): Promise<void> {
  await Promise.all([...pendingByBoard.keys()].map((boardId) => flushBoard(boardId)));
}

function snapshot(label: string, state: Pick<EditorState, "nodes" | "edges">): HistoryFrame {
  return { label, nodes: [...state.nodes], edges: [...state.edges] };
}

function nodeSnapRect(node: EditorNode): SnapRect {
  const fallback = componentRegistry.get(node.data.kind)?.defaultSize ?? { width: 240, height: 160 };
  return {
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.measured?.width ?? node.width ?? node.initialWidth ?? fallback.width,
    height: node.measured?.height ?? node.height ?? node.initialHeight ?? fallback.height,
  };
}

function applyNodeSnapping(
  changes: NodeChange<EditorNode>[],
  nodes: EditorNode[],
): { changes: NodeChange<EditorNode>[]; guides: SnapGuide[] } {
  const session = activeSnapSession;
  if (!session) return { changes, guides: [] };
  if (session.mode === "resize") return applyNodeResizeSnapping(changes, nodes, session);
  const anchorChange = changes.find(
    (change) => change.type === "position" && change.id === session.anchorId && change.position,
  );
  if (!anchorChange || anchorChange.type !== "position" || !anchorChange.position) {
    return { changes, guides: [] };
  }
  const anchor = nodes.find((node) => node.id === session.anchorId);
  if (!anchor) return { changes, guides: [] };
  const snapped = snapNodePosition(
    nodeSnapRect(anchor),
    anchorChange.position,
    session.index,
    session.threshold,
    session.gap,
  );
  const deltaX = snapped.position.x - anchorChange.position.x;
  const deltaY = snapped.position.y - anchorChange.position.y;
  if (deltaX === 0 && deltaY === 0) return { changes, guides: snapped.guides };
  const effectiveChanges = changes.map((change) => {
    if (change.type !== "position" || !change.position || !session.movingIds.has(change.id)) return change;
    return {
      ...change,
      position: { x: change.position.x + deltaX, y: change.position.y + deltaY },
      positionAbsolute: change.positionAbsolute
        ? { x: change.positionAbsolute.x + deltaX, y: change.positionAbsolute.y + deltaY }
        : change.positionAbsolute,
    };
  });
  return { changes: effectiveChanges, guides: snapped.guides };
}

function applyNodeResizeSnapping(
  changes: NodeChange<EditorNode>[],
  nodes: EditorNode[],
  session: ActiveSnapSession,
): { changes: NodeChange<EditorNode>[]; guides: SnapGuide[] } {
  const node = nodes.find((candidate) => candidate.id === session.anchorId);
  if (!node) return { changes, guides: [] };
  const current = nodeSnapRect(node);
  const positionChange = changes.find(
    (change) => change.type === "position" && change.id === session.anchorId && change.position,
  );
  const dimensionsChange = changes.find(
    (change) => change.type === "dimensions" && change.id === session.anchorId && change.dimensions,
  );
  if (!positionChange && !dimensionsChange) return { changes, guides: [] };
  const nextPosition = positionChange?.type === "position" && positionChange.position
    ? positionChange.position
    : current;
  const nextDimensions = dimensionsChange?.type === "dimensions" && dimensionsChange.dimensions
    ? dimensionsChange.dimensions
    : current;
  const proposed = {
    x: nextPosition.x,
    y: nextPosition.y,
    width: nextDimensions.width,
    height: nextDimensions.height,
  };

  if (positionChange?.type === "position" && positionChange.position) {
    if (Math.abs(positionChange.position.x - current.x) > 0.001) session.resizeEdges.horizontal = "left";
    if (Math.abs(positionChange.position.y - current.y) > 0.001) session.resizeEdges.vertical = "top";
  }
  if (dimensionsChange?.type === "dimensions" && dimensionsChange.dimensions) {
    if (!session.resizeEdges.horizontal && Math.abs(dimensionsChange.dimensions.width - current.width) > 0.001) {
      session.resizeEdges.horizontal = "right";
    }
    if (!session.resizeEdges.vertical && Math.abs(dimensionsChange.dimensions.height - current.height) > 0.001) {
      session.resizeEdges.vertical = "bottom";
    }
  }

  const snapped = snapNodeResize(
    current,
    proposed,
    session.resizeEdges,
    session.index,
    session.threshold,
    session.gap,
  );
  if (session.keepAspectRatio && current.height > 0) {
    const aspectRatio = current.width / current.height;
    if (snapped.snappedX) {
      const bottom = current.y + current.height;
      snapped.rect.height = snapped.rect.width / aspectRatio;
      if (session.resizeEdges.vertical === "top") snapped.rect.y = bottom - snapped.rect.height;
      snapped.guides = snapped.guides.filter((guide) => guide.axis === "x");
    } else if (snapped.snappedY) {
      const right = current.x + current.width;
      snapped.rect.width = snapped.rect.height * aspectRatio;
      if (session.resizeEdges.horizontal === "left") snapped.rect.x = right - snapped.rect.width;
      snapped.guides = snapped.guides.filter((guide) => guide.axis === "y");
    }
  }
  const effectiveChanges = changes.map((change) => {
    if (!("id" in change) || change.id !== session.anchorId) return change;
    if (change.type === "position" && change.position) {
      return {
        ...change,
        position: { x: snapped.rect.x, y: snapped.rect.y },
        positionAbsolute: change.positionAbsolute
          ? { x: snapped.rect.x, y: snapped.rect.y }
          : change.positionAbsolute,
      };
    }
    if (change.type === "dimensions" && change.dimensions) {
      return {
        ...change,
        dimensions: { width: snapped.rect.width, height: snapped.rect.height },
      };
    }
    return change;
  });
  return { changes: effectiveChanges, guides: snapped.guides };
}

function nodeRectMap(nodes: readonly EditorNode[]): Map<string, SnapRect> {
  return new Map(nodes.map((node) => [node.id, nodeSnapRect(node)]));
}

function pushHistory(
  state: EditorState,
  label: string,
  groupKey = "",
): Pick<EditorState, "past" | "future"> {
  const currentTime = Date.now();
  const grouped = groupKey && groupKey === lastHistoryKey && currentTime - lastHistoryAt < TEXT_HISTORY_WINDOW_MS;
  lastHistoryKey = groupKey;
  lastHistoryAt = currentTime;
  if (grouped) return { past: state.past, future: [] };
  return {
    past: [...state.past.slice(-(HISTORY_LIMIT - 1)), snapshot(label, state)],
    future: [],
  };
}

function persistGraphDiff(
  boardId: string,
  beforeNodes: EditorNode[],
  beforeEdges: EditorEdge[],
  afterNodes: EditorNode[],
  afterEdges: EditorEdge[],
): void {
  const beforeNodeMap = new Map(beforeNodes.map((node) => [node.id, node]));
  const afterNodeMap = new Map(afterNodes.map((node) => [node.id, node]));
  const beforeEdgeMap = new Map(beforeEdges.map((edge) => [edge.id, edge]));
  const afterEdgeMap = new Map(afterEdges.map((edge) => [edge.id, edge]));
  const upsertNodes = afterNodes
    .filter((node) => beforeNodeMap.get(node.id) !== node)
    .map((node) => canvasNodeToStoredNode(node, boardId));
  const upsertEdges = afterEdges
    .filter((edge) => beforeEdgeMap.get(edge.id) !== edge)
    .map((edge) => canvasEdgeToStoredEdge(edge, boardId));
  enqueueChanges(boardId, {
    upsertNodes,
    upsertEdges,
    deleteNodeIds: beforeNodes.filter((node) => !afterNodeMap.has(node.id)).map((node) => node.id),
    deleteEdgeIds: beforeEdges.filter((edge) => !afterEdgeMap.has(edge.id)).map((edge) => edge.id),
  });
}

async function loadBoard(boardId: string): Promise<void> {
  activeSnapSession = null;
  const boardSnapshot = await getBoardSnapshot(boardId);
  if (!boardSnapshot) throw new Error("画板不存在或已经被删除。");
  const boards = await kimnoteDb.boards.where("projectId").equals(boardSnapshot.project.id).sortBy("order");
  const timestamp = nowIso();
  const project = { ...boardSnapshot.project, activeBoardId: boardId, lastOpenedAt: timestamp };
  await kimnoteDb.projects.update(project.id, { activeBoardId: boardId, lastOpenedAt: timestamp });
  useEditorStore.setState({
    project,
    board: boardSnapshot.board,
    boards,
    nodes: boardSnapshot.nodes.map((node) => storedNodeToCanvasNode(node) as EditorNode),
    edges: boardSnapshot.edges.map((edge) => storedEdgeToCanvasEdge(edge) as EditorEdge),
    alignmentGuides: [],
    past: [],
    future: [],
    error: null,
    status: "ready",
  });
}

async function createLinkedBoardForPortal(sourceBoard: BoardRecord, node: EditorNode): Promise<void> {
  try {
    const linkedBoard = await createBoardRecord({
      projectId: sourceBoard.projectId,
      parentId: sourceBoard.id,
      title: node.data.title || "新画板",
      activate: false,
    });
    const updatedNode: EditorNode = {
      ...node,
      data: { ...node.data, title: linkedBoard.title, targetBoardId: linkedBoard.id, updatedAt: nowIso() },
    };
    useEditorStore.setState((state) => state.board?.id === sourceBoard.id
      ? {
          nodes: state.nodes.map((item) => item.id === node.id ? updatedNode : item),
          boards: [...state.boards, linkedBoard],
        }
      : state);
    enqueueChanges(sourceBoard.id, {
      upsertNodes: [canvasNodeToStoredNode(updatedNode, sourceBoard.id)],
    });
    await flushBoard(sourceBoard.id);
  } catch (error) {
    useEditorStore.setState({ error: error instanceof Error ? error.message : "无法创建画板入口" });
  }
}

function welcomeNodes(): EditorNode[] {
  const positions: Array<[string, XYPosition, Partial<EditorNodeData>]> = [
    ["heading", { x: 40, y: 30 }, { content: "欢迎来到 Kimnote" }],
    ["note", { x: 55, y: 165 }, { content: "这是你的自由画布。拖动卡片，把想法放到任何位置。", color: "butter" }],
    ["checklist", { x: 355, y: 145 }, { title: "试试这些操作" }],
    ["link", { x: 690, y: 170 }, { title: "收集灵感", url: "https://milanote.com" }],
    ["text", { x: 55, y: 430 }, { content: "双击空白处，新建便签。\n按住空格拖动画布，滚轮缩放。" }],
  ];
  return positions.flatMap(([kind, position, overrides]) => {
    const plugin = componentRegistry.get(kind);
    return plugin ? [createEditorNode(plugin, position, overrides)] : [];
  });
}

export const useEditorStore = create<EditorState>((set, get) => ({
  status: "idle",
  error: null,
  projects: [],
  boards: [],
  project: null,
  board: null,
  nodes: [],
  edges: [],
  saving: false,
  past: [],
  future: [],
  clipboard: null,
  alignmentGuides: [],

  boot: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const wasEmpty = (await kimnoteDb.projects.count()) === 0;
      const created = await initializeDefaultProject();
      if (wasEmpty) {
        const timestamp = nowIso();
        const project = { ...created.project, title: "我的灵感空间", updatedAt: timestamp };
        const board = { ...created.board, title: "灵感画板", updatedAt: timestamp };
        const nodes = welcomeNodes();
        const edges: EditorEdge[] = nodes.length >= 3 ? [
          {
            id: crypto.randomUUID(),
            type: "canvasEdge",
            source: nodes[1].id,
            target: nodes[2].id,
            data: { createdAt: timestamp, updatedAt: timestamp, color: "blue" },
          },
        ] : [];
        await kimnoteDb.transaction("rw", kimnoteDb.projects, kimnoteDb.boards, async () => {
          await kimnoteDb.projects.put(project);
          await kimnoteDb.boards.put(board);
        });
        await applyBoardChanges(board.id, {
          upsertNodes: nodes.map((node) => canvasNodeToStoredNode(node, board.id)),
          upsertEdges: edges.map((edge) => canvasEdgeToStoredEdge(edge, board.id)),
          viewport: { x: 80, y: 55, zoom: 0.86 },
        });
      }
      await loadBoard(created.board.id);
      await get().refreshLibrary();
    } catch (error) {
      set({ status: "error", error: error instanceof Error ? error.message : "无法打开本地数据库" });
    }
  },

  refreshLibrary: async () => {
    const projects = await kimnoteDb.projects.orderBy("lastOpenedAt").reverse().toArray();
    const currentProjectId = get().project?.id;
    const boards = currentProjectId
      ? await kimnoteDb.boards.where("projectId").equals(currentProjectId).sortBy("order")
      : [];
    set({ projects, boards });
  },

  createProject: async (title = "新项目") => {
    await flushAll();
    const created = await createDefaultProject({ title, boardTitle: "主画板" });
    await loadBoard(created.board.id);
    await get().refreshLibrary();
  },

  selectProject: async (projectId) => {
    if (projectId === get().project?.id) return;
    await flushAll();
    const project = await kimnoteDb.projects.get(projectId);
    if (!project) return;
    await loadBoard(project.activeBoardId);
    await get().refreshLibrary();
  },

  renameProject: (title) => {
    const project = get().project;
    if (!project) return;
    const next = { ...project, title: title.trimStart(), updatedAt: nowIso() };
    set((state) => ({
      project: next,
      projects: state.projects.map((item) => item.id === next.id ? next : item),
    }));
    void kimnoteDb.projects.put(next);
  },

  createBoard: async (title = "新画板") => {
    const project = get().project;
    if (!project) return;
    await flushAll();
    const board = await createBoardRecord({ projectId: project.id, title, activate: true });
    await loadBoard(board.id);
    await get().refreshLibrary();
  },

  selectBoard: async (boardId) => {
    if (boardId === get().board?.id) return;
    await flushAll();
    await loadBoard(boardId);
  },

  renameBoard: (title) => {
    const board = get().board;
    if (!board) return;
    const next = { ...board, title: title.trimStart(), updatedAt: nowIso() };
    set((state) => ({
      board: next,
      boards: state.boards.map((item) => item.id === next.id ? next : item),
    }));
    void kimnoteDb.boards.put(next);
  },

  moveBoard: async (boardId, parentId) => {
    await flushAll();
    const updated = await setBoardParent(boardId, parentId);
    set((state) => ({
      board: state.board?.id === updated.id ? updated : state.board,
      boards: state.boards.map((item) => item.id === updated.id ? updated : item),
    }));
  },

  duplicateBoard: async (boardId) => {
    await flushAll();
    const result = await duplicateBoardTree(boardId);
    await get().refreshLibrary();
    return result.root;
  },

  deleteBoard: async (boardId) => {
    await flushAll();
    const currentBoardId = get().board?.id;
    const result = await deleteBoardRecord(boardId);
    await loadBoard(currentBoardId === boardId ? result.nextBoardId : currentBoardId ?? result.nextBoardId);
    await get().refreshLibrary();
  },

  addNode: (kind, position = { x: 120, y: 100 }, overrides) => {
    const board = get().board;
    const plugin = componentRegistry.get(kind);
    if (!board || !plugin || plugin.declarations.availability?.creatable === false) return null;
    const node = createEditorNode(plugin, position, overrides);
    set((state) => {
      const history = pushHistory(state, `添加${plugin.title}`);
      return { ...history, nodes: [...state.nodes, node] };
    });
    enqueueChanges(board.id, { upsertNodes: [canvasNodeToStoredNode(node, board.id)] });
    void flushBoard(board.id);
    if (kind === "board" && !node.data.targetBoardId) {
      void createLinkedBoardForPortal(board, node);
    }
    return node;
  },

  addFiles: async (files, position) => {
    const project = get().project;
    if (!project) return;
    let offset = 0;
    for (const file of files) {
      const asset = await putAssetBlob(project.id, file, { originalName: file.name, mimeType: file.type });
      const isImage = file.type.startsWith("image/");
      get().addNode(isImage ? "image" : "file", { x: position.x + offset, y: position.y + offset }, {
        title: isImage ? "" : file.name,
        assetHash: asset.hash,
        fileName: file.name,
        mimeType: asset.mimeType,
        fileSize: asset.byteLength,
      });
      offset += 28;
    }
  },

  updateNodeData: (id, patch, historyLabel = "编辑组件") => {
    const board = get().board;
    if (!board) return;
    let updated: EditorNode | undefined;
    set((state) => {
      const history = pushHistory(state, historyLabel, `node-data:${id}`);
      const nodes = state.nodes.map((node) => {
        if (node.id !== id) return node;
        const nextData: EditorNodeData = { ...node.data, ...patch, updatedAt: nowIso() };
        for (const [key, value] of Object.entries(nextData)) {
          if (value === undefined) delete nextData[key];
        }
        updated = {
          ...node,
          data: nextData,
        };
        return updated;
      });
      return { ...history, nodes };
    });
    if (updated) enqueueChanges(board.id, { upsertNodes: [canvasNodeToStoredNode(updated, board.id)] });
  },

  updateEdge: (id, patch) => {
    const board = get().board;
    if (!board) return;
    let updated: EditorEdge | undefined;
    set((state) => {
      const history = pushHistory(state, "编辑连线", `edge-data:${id}`);
      const edges = state.edges.map((edge) => {
        if (edge.id !== id) return edge;
        const { animated, ...dataPatch } = patch;
        updated = {
          ...edge,
          animated: animated ?? edge.animated,
          data: { ...edge.data, ...dataPatch, updatedAt: nowIso() },
        };
        return updated;
      });
      return { ...history, edges };
    });
    if (updated) enqueueChanges(board.id, { upsertEdges: [canvasEdgeToStoredEdge(updated, board.id)] });
  },

  onNodesChange: (changes) => {
    const board = get().board;
    if (!board) return;
    const removedIds = changes.filter((change) => change.type === "remove").map((change) => change.id);
    const structuralChange = removedIds.length > 0;
    if (structuralChange) {
      set((state) => ({ ...pushHistory(state, "删除组件"), nodes: applyNodeChanges(changes, state.nodes) }));
      enqueueChanges(board.id, { deleteNodeIds: removedIds });
      void flushBoard(board.id);
      return;
    }
    const currentState = get();
    const snapping = applyNodeSnapping(changes, currentState.nodes);
    const changedNodeIds = new Set(
      snapping.changes.flatMap((change) =>
        change.type === "position" || change.type === "dimensions" ? [change.id] : [],
      ),
    );
    const nextNodes = applyNodeChanges(snapping.changes, currentState.nodes);
    const nextEdges = reassignConnectionHandles(
      currentState.edges,
      nodeRectMap(nextNodes),
      changedNodeIds,
    );
    set({ nodes: nextNodes, edges: nextEdges, alignmentGuides: snapping.guides });
    const settledIds = new Set(
      changes.flatMap((change) => {
        if (change.type === "position" && change.dragging === false) return [change.id];
        if (change.type === "dimensions" && !change.resizing) return [change.id];
        return [];
      }),
    );
    if (settledIds.size) {
      const nodes = get().nodes.filter((node) => settledIds.has(node.id));
      const changedEdges = get().edges.filter((edge) =>
        settledIds.has(edge.source) || settledIds.has(edge.target),
      );
      enqueueChanges(board.id, {
        upsertNodes: nodes.map((node) => canvasNodeToStoredNode(node, board.id)),
        upsertEdges: changedEdges.map((edge) => canvasEdgeToStoredEdge(edge, board.id)),
      });
      void flushBoard(board.id);
    }
  },

  onEdgesChange: (changes) => {
    const board = get().board;
    if (!board) return;
    const removedIds = changes.filter((change) => change.type === "remove").map((change) => change.id);
    if (removedIds.length) {
      set((state) => ({ ...pushHistory(state, "删除连线"), edges: applyEdgeChanges(changes, state.edges) }));
      enqueueChanges(board.id, { deleteEdgeIds: removedIds });
      void flushBoard(board.id);
    } else {
      set((state) => ({ edges: applyEdgeChanges(changes, state.edges) }));
    }
  },

  onConnect: (connection) => {
    const board = get().board;
    if (!board || !connection.source || !connection.target || connection.source === connection.target) return;
    const rects = nodeRectMap(get().nodes);
    const sourceRect = rects.get(connection.source);
    const targetRect = rects.get(connection.target);
    const handles = sourceRect && targetRect
      ? bestConnectionHandles(sourceRect, targetRect)
      : { sourceHandle: connection.sourceHandle, targetHandle: connection.targetHandle };
    const timestamp = nowIso();
    const edge: EditorEdge = {
      id: crypto.randomUUID(),
      type: "canvasEdge",
      source: connection.source,
      target: connection.target,
      sourceHandle: handles.sourceHandle,
      targetHandle: handles.targetHandle,
      data: { createdAt: timestamp, updatedAt: timestamp, color: "slate" },
    };
    set((state) => ({ ...pushHistory(state, "创建连线"), edges: addEdge(edge, state.edges) }));
    enqueueChanges(board.id, { upsertEdges: [canvasEdgeToStoredEdge(edge, board.id)] });
    void flushBoard(board.id);
  },

  beginGesture: (label) => {
    const state = get();
    activeGesture = snapshot(label, state);
    lastHistoryKey = "";
  },

  commitGesture: () => {
    if (!activeGesture) return;
    const state = get();
    const before = activeGesture;
    activeGesture = null;
    const changed = before.nodes.some((node, index) => state.nodes[index] !== node) || before.nodes.length !== state.nodes.length;
    if (!changed) return;
    set({ past: [...state.past.slice(-(HISTORY_LIMIT - 1)), before], future: [] });
  },

  beginNodeDrag: (nodeId, snapThreshold, snapGap) => {
    const state = get();
    const anchor = state.nodes.find((node) => node.id === nodeId);
    if (!anchor) return;
    const movingNodes = anchor.selected
      ? state.nodes.filter((node) => node.selected)
      : [anchor];
    const movingIds = new Set(movingNodes.map((node) => node.id));
    activeSnapSession = {
      anchorId: nodeId,
      movingIds,
      index: new NodeSnapIndex(
        state.nodes.filter((node) => !movingIds.has(node.id)).map(nodeSnapRect),
      ),
      threshold: Math.max(0, snapThreshold),
      gap: Math.max(0, snapGap),
      mode: "drag",
      resizeEdges: { horizontal: null, vertical: null },
      keepAspectRatio: false,
    };
    get().beginGesture("移动组件");
  },

  endNodeDrag: () => {
    activeSnapSession = null;
    set({ alignmentGuides: [] });
    get().commitGesture();
  },

  beginNodeResize: (nodeId, snapThreshold, snapGap, keepAspectRatio = false) => {
    const state = get();
    if (!state.nodes.some((node) => node.id === nodeId)) return;
    activeSnapSession = {
      anchorId: nodeId,
      movingIds: new Set([nodeId]),
      index: new NodeSnapIndex(
        state.nodes.filter((node) => node.id !== nodeId).map(nodeSnapRect),
      ),
      threshold: Math.max(0, snapThreshold),
      gap: Math.max(0, snapGap),
      mode: "resize",
      resizeEdges: { horizontal: null, vertical: null },
      keepAspectRatio,
    };
    get().beginGesture("调整组件大小");
  },

  endNodeResize: () => {
    activeSnapSession = null;
    set({ alignmentGuides: [] });
    get().commitGesture();
  },

  saveViewport: (viewport) => {
    const board = get().board;
    if (!board) return;
    set({ board: { ...board, viewport: { ...viewport } } });
    enqueueChanges(board.id, { viewport });
  },

  deleteSelected: () => {
    const state = get();
    const board = state.board;
    if (!board) return;
    const nodeIds = state.nodes.filter((node) => node.selected).map((node) => node.id);
    const edgeIds = state.edges
      .filter((edge) => edge.selected || nodeIds.includes(edge.source) || nodeIds.includes(edge.target))
      .map((edge) => edge.id);
    if (!nodeIds.length && !edgeIds.length) return;
    set({
      ...pushHistory(state, "删除所选"),
      nodes: state.nodes.filter((node) => !nodeIds.includes(node.id)),
      edges: state.edges.filter((edge) => !edgeIds.includes(edge.id)),
    });
    enqueueChanges(board.id, { deleteNodeIds: nodeIds, deleteEdgeIds: edgeIds });
    void flushBoard(board.id);
  },

  duplicateSelected: async () => {
    const initial = get();
    const selectedNodes = initial.nodes.filter((node) => node.selected);
    if (!selectedNodes.length) return;
    const targetBoardIds = [...new Set(selectedNodes.flatMap((node) =>
      node.data.kind === "board" && node.data.targetBoardId ? [node.data.targetBoardId] : [],
    ))];
    if (!targetBoardIds.length) {
      get().copySelected();
      get().pasteClipboard();
      return;
    }

    await flushAll();
    const boardIdMap = new Map<string, string>();
    for (const targetBoardId of targetBoardIds) {
      const duplicated = await duplicateBoardTree(targetBoardId);
      boardIdMap.set(targetBoardId, duplicated.root.id);
    }
    await get().refreshLibrary();

    const state = get();
    if (!state.board || state.board.id !== initial.board?.id) return;
    const selectedIds = new Set(selectedNodes.map((node) => node.id));
    const graphNodes = selectedNodes.map((node) => ({
      ...node,
      data: node.data.targetBoardId && boardIdMap.has(node.data.targetBoardId)
        ? { ...node.data, targetBoardId: boardIdMap.get(node.data.targetBoardId) }
        : node.data,
    }));
    const graphEdges = initial.edges.filter((edge) => selectedIds.has(edge.source) && selectedIds.has(edge.target));
    const anchor = {
      x: Math.min(...graphNodes.map((node) => node.position.x)),
      y: Math.min(...graphNodes.map((node) => node.position.y)),
    };
    const previousClipboard = state.clipboard;
    set({ clipboard: { anchor, nodes: graphNodes, edges: graphEdges } });
    get().pasteClipboard();
    set({ clipboard: previousClipboard });
  },

  copySelected: () => {
    const state = get();
    const nodes = state.nodes.filter((node) => node.selected);
    if (!nodes.length) return;
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = state.edges.filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target));
    const anchor = {
      x: Math.min(...nodes.map((node) => node.position.x)),
      y: Math.min(...nodes.map((node) => node.position.y)),
    };
    set({ clipboard: { anchor, nodes, edges } });
  },

  pasteClipboard: (position) => {
    const state = get();
    const board = state.board;
    const clipboard = state.clipboard;
    if (!board || !clipboard) return;
    const origin = position ?? { x: clipboard.anchor.x + 32, y: clipboard.anchor.y + 32 };
    const idMap = new Map<string, string>();
    const timestamp = nowIso();
    const nodes = clipboard.nodes.map((node) => {
      const id = crypto.randomUUID();
      idMap.set(node.id, id);
      return {
        ...node,
        id,
        position: {
          x: origin.x + node.position.x - clipboard.anchor.x,
          y: origin.y + node.position.y - clipboard.anchor.y,
        },
        selected: true,
        data: { ...node.data, createdAt: timestamp, updatedAt: timestamp },
      };
    });
    const edges = clipboard.edges.map((edge) => ({
      ...edge,
      id: crypto.randomUUID(),
      source: idMap.get(edge.source)!,
      target: idMap.get(edge.target)!,
      selected: false,
      data: { ...edge.data, createdAt: timestamp, updatedAt: timestamp },
    }));
    const nextNodes = [...state.nodes.map((node) => ({ ...node, selected: false })), ...nodes];
    const nextEdges = [...state.edges, ...edges];
    set({ ...pushHistory(state, "粘贴组件"), nodes: nextNodes, edges: nextEdges });
    enqueueChanges(board.id, {
      upsertNodes: nodes.map((node) => canvasNodeToStoredNode(node, board.id)),
      upsertEdges: edges.map((edge) => canvasEdgeToStoredEdge(edge, board.id)),
    });
    void flushBoard(board.id);
  },

  selectAll: () => {
    set((state) => ({ nodes: state.nodes.map((node) => ({ ...node, selected: true })) }));
  },

  clearSelection: () => {
    set((state) => ({
      nodes: state.nodes.map((node) => node.selected ? { ...node, selected: false } : node),
      edges: state.edges.map((edge) => edge.selected ? { ...edge, selected: false } : edge),
    }));
  },

  undo: () => {
    const state = get();
    const board = state.board;
    const previous = state.past.at(-1);
    if (!board || !previous) return;
    const current = snapshot(previous.label, state);
    set({
      nodes: previous.nodes,
      edges: previous.edges,
      past: state.past.slice(0, -1),
      future: [current, ...state.future].slice(0, HISTORY_LIMIT),
    });
    persistGraphDiff(board.id, state.nodes, state.edges, previous.nodes, previous.edges);
    void flushBoard(board.id);
    lastHistoryKey = "";
  },

  redo: () => {
    const state = get();
    const board = state.board;
    const next = state.future[0];
    if (!board || !next) return;
    const current = snapshot(next.label, state);
    set({
      nodes: next.nodes,
      edges: next.edges,
      past: [...state.past, current].slice(-HISTORY_LIMIT),
      future: state.future.slice(1),
    });
    persistGraphDiff(board.id, state.nodes, state.edges, next.nodes, next.edges);
    void flushBoard(board.id);
    lastHistoryKey = "";
  },

  flush: flushAll,
}));

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => void flushAll());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushAll();
  });
}
